import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import styles from './ChatPage.module.css';
import { ChatWindow } from '../components/library/chat-interface/ChatWindow';
import { ChatInput } from '../components/library/chat-interface/ChatInput';
import { SessionUpdateBanner } from '../components/library/banners/SessionUpdateBanner';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useLocalSession } from '../contexts/LocalSessionContext';
import { useGlobalSession } from '../contexts/GlobalSessionContext';
import { v4 as uuidv4 } from 'uuid';
import logger from '../logging/logger';
import { buildManualOrderPrefillFromDraft } from '../features/order-ticket/manual/manualOrderDraftPrefill';
import { MessageFeedback, SessionService } from '../services/SessionService';

interface SessionChatPageProps {
  chatHash: string;
  sidebarCollapsed: boolean;
  onScrollDirectionChange?: (isScrollingDown: boolean) => void;
}

// Helpers to normalize websocket payloads
const normalizeWsContent = (payload: any): { text: string; obj?: any } => {
  const c = payload?.content;

  if (typeof payload?.delta === 'string') return { text: payload.delta };
  if (typeof c === 'string') return { text: c };
  if (c && typeof c === 'object') return { text: c.text ?? '', obj: c };

  // fallback: sometimes backend sends `text` directly
  if (typeof payload?.text === 'string') return { text: payload.text };
  return { text: '' };
};

const normalizeChartPayload = (payload: any) => {
  // backend commonly sends { type:'chart_data', data: ... }
  if (payload?.data !== undefined) return payload.data;
  if (payload?.chart !== undefined) return payload.chart;
  return payload;
};

export const SessionChatPage: React.FC<SessionChatPageProps> = ({ chatHash, sidebarCollapsed, onScrollDirectionChange }) => {
  const [isWebSocketReady, setIsWebSocketReady] = useState(false);
  const [pendingInitialMessage, setPendingInitialMessage] = useState<string | null>(null);
  const [isSessionBusy, setIsSessionBusy] = useState(false);
  const [hasSessionUpdates, setHasSessionUpdates] = useState(false);
  const [agentActivity, setAgentActivity] = useState<string | null>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { user, userUuid, isAuthenticated, getToken } = useAuth();
  const sessionService = useMemo(() => new SessionService(), []);

  const {
    sendMessage,
    stopGeneration,
    subscribeToSession,
  } = useWebSocket();

  const {
    messages,
    isResponding,
    isWaitingForResponse,
    isLoadingMessages,
    sessionExists,
    hasMoreMessages,
    isLoadingMoreMessages,
    setSessionId,
    addMessage,
    updateMessage,
    upsertMessage,
    removeMessage,
    setWaitingForResponse,
    startResponding,
    stopResponding,
    appendToStreamingMessage,
    completeStreamingMessage,
    loadSession,
    loadMoreMessages,
    initializeFromHandoff,
  } = useLocalSession();

  const {
    currentSession,
    selectSession,
    generateTitleFromHistory,
    updateSessionTitleLocally,
  } = useGlobalSession();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContentRef = useRef<HTMLDivElement>(null);
  const lastScrollTopRef = useRef(0);
  const initialMessageRef = useRef<string | null>(null);
  const showUserMessageImmediatelyRef = useRef<boolean>(false);

  const hasDisplayedInitialUserMessageRef = useRef<boolean>(false);
  const hasSentInitialMessageRef = useRef<boolean>(false);
  const isTitleGenerationInProgressRef = useRef<boolean>(false);
  const hasInitializedFromHandoffRef = useRef<boolean>(false);
  const messagesRef = useRef(messages);
  const feedbackSaveInFlightRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const createMessage = useCallback((content: string, role: 'user' | 'assistant') => ({
    id: uuidv4(),
    persistedMessageId: null,
    content,
    contentObj: undefined as any,
    role,
    timestamp: new Date().toISOString(),
    sender: role,
    feedback: null,
    hasQueuedFeedback: false,
    feedbackSyncState: 'synced' as const,
  }), []);

  // IMPORTANT: keep structured payloads for charts
  const formattedMessages = useMemo(() => {
    return messages.map((msg: any) => {
      // Debug logging for backtest_result messages
      if (msg.contentObj?.backtest_result) {
        logger.info('📨 SessionChatPage: Message with backtest_result:', {
          id: msg.id,
          backtest_result: msg.contentObj.backtest_result,
        });
      }
      return {
        id: msg.id,
        user: msg.role === 'user' ? 'user' : 'AI',
        text: msg.content,
        feedback: msg.feedback ?? null,
        content: msg.contentObj, // <— ChatWindow can read chart/data from here
        timestamp: msg.timestamp,
      };
    });
  }, [messages]);

  const persistQueuedFeedback = useCallback(async (
    localMessageId: string,
    persistedMessageId: string,
    feedbackToPersist: MessageFeedback | null
  ): Promise<void> => {
    if (!chatHash || !userUuid) return;
    if (feedbackSaveInFlightRef.current.has(localMessageId)) return;

    feedbackSaveInFlightRef.current.add(localMessageId);
    try {
      const token = await getToken();
      if (!token) {
        logger.warn('Feedback save deferred: missing auth token', { localMessageId, persistedMessageId });
        return;
      }

      await sessionService.updateMessageFeedback(chatHash, persistedMessageId, userUuid, token, feedbackToPersist);

      const latest = messagesRef.current.find((m: any) => m.id === localMessageId);
      if (!latest) return;
      if (latest.hasQueuedFeedback && latest.queuedFeedback !== feedbackToPersist) {
        return;
      }

      updateMessage(localMessageId, {
        hasQueuedFeedback: false,
        queuedFeedback: undefined,
        feedbackSyncState: 'synced',
      });
      logger.info('Feedback persisted successfully', { localMessageId, persistedMessageId, feedbackToPersist });
    } catch (error: any) {
      logger.error('Failed to persist queued feedback', {
        localMessageId,
        persistedMessageId,
        feedbackToPersist,
        status: error?.status ?? null,
        error: error?.message || String(error),
      });
      updateMessage(localMessageId, {
        hasQueuedFeedback: true,
        queuedFeedback: feedbackToPersist,
        feedbackSyncState: 'failed',
      });
    } finally {
      feedbackSaveInFlightRef.current.delete(localMessageId);
      const latest = messagesRef.current.find((m: any) => m.id === localMessageId);
      if (
        latest?.persistedMessageId &&
        latest?.hasQueuedFeedback &&
        latest?.feedbackSyncState === 'pending' &&
        latest?.queuedFeedback !== feedbackToPersist &&
        !feedbackSaveInFlightRef.current.has(localMessageId)
      ) {
        void persistQueuedFeedback(
          localMessageId,
          latest.persistedMessageId,
          latest.queuedFeedback ?? null
        );
      }
    }
  }, [chatHash, userUuid, getToken, sessionService, updateMessage]);

  const handleMessageFeedbackChange = useCallback(async (
    messageId: string,
    feedback: MessageFeedback | null
  ): Promise<boolean> => {
    const targetMessage = messagesRef.current.find((msg: any) => msg.id === messageId);
    if (targetMessage && targetMessage.role !== 'assistant') {
      return false;
    }

    updateMessage(messageId, {
      feedback,
      hasQueuedFeedback: true,
      queuedFeedback: feedback,
      feedbackSyncState: 'pending',
    });

    const persistedMessageId = targetMessage?.persistedMessageId;
    if (!persistedMessageId) {
      logger.info('Feedback queued until message persistence completes', {
        localMessageId: messageId,
        persistedMessageId: null,
        feedback,
      });
      return true;
    }

    void persistQueuedFeedback(messageId, persistedMessageId, feedback);
    return true;
  }, [updateMessage, persistQueuedFeedback]);

  useEffect(() => {
    if (!userUuid) return;
    messagesRef.current.forEach((message: any) => {
      if (
        message.role === 'assistant' &&
        message.persistedMessageId &&
        message.hasQueuedFeedback &&
        message.feedbackSyncState === 'pending' &&
        !feedbackSaveInFlightRef.current.has(message.id)
      ) {
        void persistQueuedFeedback(
          message.id,
          message.persistedMessageId,
          message.queuedFeedback ?? null
        );
      }
    });
  }, [userUuid, persistQueuedFeedback, messages]);

  useEffect(() => {
    if (location.state) {
      const { initialMessage, showUserMessageImmediately } = location.state as any;
      if (initialMessage) {
        initialMessageRef.current = initialMessage;
        showUserMessageImmediatelyRef.current = showUserMessageImmediately || false;
      }
    }
  }, [location.state]);

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    // Use the ChatWindow's internal scroll function if available
    if ((window as any).__chatWindowScrollToBottom) {
      (window as any).__chatWindowScrollToBottom();
    } else if (chatContentRef.current) {
      // Fallback to scrolling the content container
      chatContentRef.current.scrollTo({
        top: chatContentRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // Scroll direction tracking for mobile header hide/show
  useEffect(() => {
    const chatContent = chatContentRef.current;
    if (!chatContent || !onScrollDirectionChange) return;

    const handleScroll = () => {
      const currentScrollTop = chatContent.scrollTop;
      const scrollThreshold = 10; // Minimum scroll distance to trigger
      
      // Only trigger if scrolled more than threshold
      if (Math.abs(currentScrollTop - lastScrollTopRef.current) > scrollThreshold) {
        const isScrollingDown = currentScrollTop > lastScrollTopRef.current;
        
        // Show header when at top or scrolling up
        if (currentScrollTop <= 10) {
          onScrollDirectionChange(false);
        } else {
          onScrollDirectionChange(isScrollingDown);
        }
        
        lastScrollTopRef.current = currentScrollTop;
      }
    };

    chatContent.addEventListener('scroll', handleScroll, { passive: true });
    return () => chatContent.removeEventListener('scroll', handleScroll);
  }, [onScrollDirectionChange]);

  useEffect(() => {
    if (chatHash) setSessionId(chatHash);
    return () => setSessionId(null);
  }, [chatHash, setSessionId]);

  // Dispatch streaming state changes to lock/unlock SideConfigMenu
  // Lock when agent is working (waiting for response OR actively streaming)
  useEffect(() => {
    const isAgentWorking = isWaitingForResponse || isResponding;
    window.dispatchEvent(new CustomEvent('streamingStateChange', {
      detail: { isStreaming: isAgentWorking }
    }));
  }, [isWaitingForResponse, isResponding]);

  // Listen for backtest progress events - single message that updates in place
  useEffect(() => {
    // Handle progress updates (create or update the progress message atomically)
    const handleBacktestProgress = (event: CustomEvent<{
      progressId: string;
      progress: number;
      phase: 'planning' | 'loading_data' | 'executing' | 'loading_positions' | 'complete' | 'failed';
      currentPeriod?: string;
      currentSymbol?: string;
      error?: string;
      results?: unknown;  // Backtest results for chart rendering (legacy or enriched payload)
    }>) => {
      const { progressId, progress, phase, currentPeriod, currentSymbol, error, results } = event.detail;
      logger.info('📊 [SessionChatPage] Backtest progress:', { progressId, progress, phase, currentPeriod, currentSymbol, hasResults: !!results });

      // Use upsertMessage for atomic add-or-update (avoids stale closure issues)
      upsertMessage({
        id: progressId,
        content: '', // No text content - progress bar only
        contentObj: {
          backtest_progress: {
            progress,
            phase,
            currentPeriod,
            currentSymbol,
            isComplete: phase === 'complete',
            error,
          },
          // Include backtest results when available (on complete)
          ...(results !== undefined ? { backtest_results: results } : {}),
        },
        role: 'assistant',
        timestamp: new Date().toISOString(),
      });
    };

    // Handle backtest result saved - adds backtest_result reference for persistence
    const handleBacktestResultSaved = (event: CustomEvent<{
      progressId: string;
      backtestConfigId: string;
      resultsDataId: string;
    }>) => {
      const { progressId, backtestConfigId, resultsDataId } = event.detail;
      logger.info('💾 [SessionChatPage] Backtest result saved:', { progressId, backtestConfigId, resultsDataId });

      // Update the existing progress message with backtest_result reference
      // This allows persisted messages to fetch results by data_id
      updateMessage(progressId, {
        contentObj: {
          backtest_result: {
            backtest_config_id: backtestConfigId,
            results_data_id: resultsDataId,
          },
          // Also add to new_data_ids so backtest results are reusable
          // through the same data_id system as other tool outputs
          new_data_ids: {
            [resultsDataId]: `Backtest results for config ${backtestConfigId}`,
          },
        },
      });
    };

    const handleBacktestAssistantMessage = (event: CustomEvent<{
      progressId?: string;
      messageId: string;
      text: string;
    }>) => {
      const { progressId, messageId, text } = event.detail;
      if (!messageId || !text) return;

      logger.info('💬 [SessionChatPage] Backtest assistant message:', { progressId, messageId });
      if (progressId) {
        removeMessage(progressId);
      }
      upsertMessage({
        id: messageId,
        content: text,
        contentObj: undefined,
        role: 'assistant',
        sender: 'assistant',
        timestamp: new Date().toISOString(),
      });
    };

    // Remove an in-progress backtest bar when the run is aborted before it starts
    // (e.g. a name conflict that needs the user to rename or overwrite).
    const handleBacktestProgressRemove = (event: CustomEvent<{ progressId: string }>) => {
      const { progressId } = event.detail;
      if (!progressId) return;
      logger.info('🧹 [SessionChatPage] Removing backtest progress bar:', progressId);
      removeMessage(progressId);
    };

    window.addEventListener('backtestProgress', handleBacktestProgress as EventListener);
    window.addEventListener('backtestResultSaved', handleBacktestResultSaved as EventListener);
    window.addEventListener(
      'backtestAssistantMessage',
      handleBacktestAssistantMessage as EventListener
    );
    window.addEventListener('backtestProgressRemove', handleBacktestProgressRemove as EventListener);
    return () => {
      window.removeEventListener('backtestProgress', handleBacktestProgress as EventListener);
      window.removeEventListener('backtestResultSaved', handleBacktestResultSaved as EventListener);
      window.removeEventListener(
        'backtestAssistantMessage',
        handleBacktestAssistantMessage as EventListener
      );
      window.removeEventListener('backtestProgressRemove', handleBacktestProgressRemove as EventListener);
    };
  }, [removeMessage, upsertMessage, updateMessage]);

  // Track if we've already loaded this session to prevent duplicate loads
  const hasLoadedSessionRef = useRef<string | null>(null);

  useEffect(() => {
    const loadSessionData = async () => {
      if (!chatHash) return;

      // Check if we're coming from NewChatPage with handoff state FIRST
      // This needs to happen before auth check to avoid flicker
      const fromNewChat = (location.state as any)?.fromNewChat === true;
      const initialMessages = (location.state as any)?.initialMessages;
      const handoffIsResponding = (location.state as any)?.isResponding;
      const handoffIsWaitingForResponse = (location.state as any)?.isWaitingForResponse;
      const handoffStreamingMessageId = (location.state as any)?.currentStreamingMessageId;

      // Detect page refresh - on refresh we should NOT use handoff state (it's stale)
      const navEntry = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
      const isPageRefresh = navEntry?.type === 'reload' || (performance as any).navigation?.type === 1;

      // If coming from NewChatPage with initial messages AND not a page refresh, use handoff immediately
      // This avoids any flicker by not waiting for auth
      if (fromNewChat && initialMessages && initialMessages.length > 0 && !hasInitializedFromHandoffRef.current && !isPageRefresh) {
        logger.info('🔄 [SessionChatPage] Initializing from NewChatPage handoff with', initialMessages.length, 'messages');
        hasInitializedFromHandoffRef.current = true;
        hasLoadedSessionRef.current = chatHash;

        initializeFromHandoff({
          messages: initialMessages,
          isResponding: handoffIsResponding,
          isWaitingForResponse: handoffIsWaitingForResponse,
          currentStreamingMessageId: handoffStreamingMessageId,
        });

        // Select session in GlobalSessionContext (this is async but we don't need to wait)
        selectSession(chatHash);
        return;
      }

      // For non-handoff cases, we need auth to load from API
      if (!isAuthenticated || !user) return;

      // Prevent duplicate loads for the same session, but only if we actually have session data
      if (hasLoadedSessionRef.current === chatHash && sessionExists === true) {
        logger.info('⏭️ [SessionChatPage] Session already loaded, skipping:', chatHash);
        return;
      }

      // On page refresh with stale handoff state, clear it
      if (isPageRefresh && fromNewChat) {
        logger.info('🔄 [SessionChatPage] Page refresh detected, loading from API');
        window.history.replaceState({}, document.title);
      }

      // Mark as loading to prevent duplicate calls
      hasLoadedSessionRef.current = chatHash;

      // Load local session data (messages)
      // If coming from NewChatPage, retry with delay to handle race condition
      let success = await loadSession(chatHash);

      if (!success && fromNewChat) {
        // Session was just created - retry after a short delay to handle race condition
        logger.info('🔄 [SessionChatPage] Session load failed, retrying (from NewChat transition)...');
        await new Promise(resolve => setTimeout(resolve, 500));
        success = await loadSession(chatHash);

        if (!success) {
          // Second retry with longer delay
          logger.info('🔄 [SessionChatPage] Retrying session load (attempt 2)...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          success = await loadSession(chatHash);
        }
      }

      if (!success) {
        logger.error('❌ Session not found:', chatHash);
        // Don't redirect immediately - let the component show the error state
        // The user can manually navigate to /chat if needed
        return;
      }

      // Also select session in GlobalSessionContext to ensure currentSession is set
      // This is important for title generation to work correctly
      await selectSession(chatHash);

      if (initialMessageRef.current && !hasDisplayedInitialUserMessageRef.current) {
        if (showUserMessageImmediatelyRef.current) {
          const userMessage = createMessage(initialMessageRef.current, 'user');
          flushSync(() => {
            addMessage(userMessage);
            hasDisplayedInitialUserMessageRef.current = true;
          });
        }

        setPendingInitialMessage(initialMessageRef.current);

        initialMessageRef.current = null;
        showUserMessageImmediatelyRef.current = false;
      }
    };

    loadSessionData();
  }, [chatHash, isAuthenticated, user, loadSession, loadMoreMessages, navigate, addMessage, selectSession, location.state, initializeFromHandoff, sessionExists]);

  // Subscribe to WebSocket for real-time streaming + charts
  useEffect(() => {
    if (!chatHash || sessionExists !== true) return;

    logger.info('🔌 Subscribing to session for real-time streaming:', chatHash);

    let currentStreamId: string | null = null;

    const unsubscribe = subscribeToSession(chatHash, {
      onSessionReady: (data: any) => {
        logger.info('✅ Session ready:', data);
        setIsWebSocketReady(true);
        setIsSessionBusy(false); // Session is ready, not busy
      },

      onSessionBusy: (_data: any) => {
        setIsSessionBusy(true);
      },

      onSessionUpdated: (data: any) => {
        logger.info('🔄 Session updated:', data);
        setHasSessionUpdates(true);
      },

      onAgentActivity: (data: any) => {
        // Update activity text (ephemeral, not persisted)
        const activity = data?.activity || null;
        setAgentActivity(activity);
      },

      onMessageStart: (data: any) => {
        logger.info('🌊 Message started:', data);
        const streamSourceId = data.message_id || uuidv4();
        const localMessageId = `assistant_stream_${streamSourceId}`;
        currentStreamId = localMessageId;

        // Clear activity when AI starts responding
        setAgentActivity(null);

        const newMessage = createMessage('', 'assistant');
        newMessage.id = localMessageId;
        newMessage.persistedMessageId = null;
        newMessage.hasQueuedFeedback = false;
        newMessage.feedbackSyncState = 'synced';

        addMessage(newMessage);
        startResponding(localMessageId);
        setIsSessionBusy(false); // We're the active client, not busy for us
      },

      onContentDelta: (data: any) => {
        const { text } = normalizeWsContent(data);
        if (currentStreamId && text) {
          appendToStreamingMessage(currentStreamId, text);
        }
      },

      onChartData: (data: any) => {
        logger.info('📊 Chart data:', data);
        const chart = normalizeChartPayload(data);

        if (!currentStreamId) return;

        // Set as embedded_chart to match what AIMessage expects
        updateMessage(currentStreamId, {
          contentObj: {
            embedded_chart: chart,
          },
        });
      },

      onBacktestConfig: async (data: any) => {
        logger.info('📋 Backtest config received:', data);
        const backtestConfigId = data?.backtest_config_id;
        const sourceTriggerMessageId = data?.source_trigger_message_id;
        if (!backtestConfigId) {
          logger.warn('No backtest_config_id in data');
          return;
        }

        try {
          // Import BacktestService dynamically to avoid circular deps
          const { backtestService } = await import('../features/order-ticket/strategy-backtest/services/BacktestService');

          // Use getToken from component scope
          const token = await getToken();
          if (!token || !user?.email) {
            logger.error('Cannot fetch backtest config: missing auth');
            return;
          }

          const config = await backtestService.getBacktestConfig(
            chatHash,
            backtestConfigId,
            user.email,
            token
          );

          if (config) {

            // First, open the side config menu with backtest tab selected
            window.dispatchEvent(new CustomEvent('openSideConfigMenu', { detail: { tab: 'backtest' } }));

            // Give the panel time to open and mount BacktestConfig, then apply config
            // Include the config_id so results can be saved back
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('applyBacktestConfig', {
                detail: {
                  config,
                  configId: backtestConfigId,
                  sourceTriggerMessageId,
                }
              }));
              logger.info('✅ Backtest config dispatched to UI with configId:', backtestConfigId);
            }, 100);
          }
        } catch (error) {
          logger.error('Failed to fetch/apply backtest config:', error);
        }
      },

      onManualOrderDraft: (data: any) => {
        logger.info('🧾 Manual order draft received:', data);
        const prefill = buildManualOrderPrefillFromDraft(data);
        if (!prefill) {
          logger.warn('Invalid manual_order_draft payload received, ignoring event', data);
          return;
        }

        window.dispatchEvent(
          new CustomEvent('openManualOrderTicket', {
            detail: { prefill },
          }),
        );
      },

      onMessageComplete: (data: any) => {
        logger.info('🏁 Message complete:', data);

        if (currentStreamId) {
          const { text, obj } = normalizeWsContent(data);
          const finalMessageId = data?.message_id || currentStreamId;
          const localMessageId = currentStreamId;
          const targetMessage = messagesRef.current.find((m: any) => m.id === localMessageId);
          const queuedFeedback = targetMessage?.hasQueuedFeedback
            ? (targetMessage?.queuedFeedback ?? null)
            : undefined;

          // If backend sends full structured content on complete, persist it
          if (obj) {
            updateMessage(localMessageId, {
              persistedMessageId: finalMessageId,
              content: text,
              contentObj: obj,
            });
          } else if (text) {
            updateMessage(localMessageId, {
              persistedMessageId: finalMessageId,
              content: text,
            });
          } else {
            updateMessage(localMessageId, { persistedMessageId: finalMessageId });
          }

          if (queuedFeedback !== undefined) {
            updateMessage(localMessageId, { feedbackSyncState: 'pending' });
            void persistQueuedFeedback(localMessageId, finalMessageId, queuedFeedback);
          }

          completeStreamingMessage(localMessageId);
          currentStreamId = null;
        }

        // Ensure lock state is released even if upstream events arrive out of order.
        setWaitingForResponse(false);
        stopResponding();
      },

      onGenerationStopped: (data: any) => {
        logger.info('⏹️ Generation stopped:', data);
        if (currentStreamId) {
          completeStreamingMessage(currentStreamId);
          currentStreamId = null;
        }
        // Reset both waiting/streaming flags to avoid stale strategy lock.
        setWaitingForResponse(false);
        stopResponding();
        setIsSessionBusy(false); // Generation stopped, session ready
      },

      onError: (data: any) => {
        logger.error('❌ WebSocket error:', data);
        stopResponding();
        setWaitingForResponse(false); // Reset waiting state on error

        // Don't show error message for disconnect events - just reset UI state
        if (data?.is_disconnect) {
          logger.info('🔌 WebSocket disconnected, resetting UI state');
          if (currentStreamId) {
            completeStreamingMessage(currentStreamId);
            currentStreamId = null;
          }
          return;
        }

        const errText = data?.message || 'Unknown error';

        if (currentStreamId) {
          updateMessage(currentStreamId, { content: `Error: ${errText}` });
          completeStreamingMessage(currentStreamId);
          currentStreamId = null;
        } else {
          addMessage(createMessage(`Error: ${errText}`, 'assistant'));
        }
      },

      onRateLimitError: (data: any) => {
        logger.warn('⚠️ Rate limit exceeded:', data);
        stopResponding();
        setWaitingForResponse(false);

        // Calculate human-readable retry time
        const retryAfterSeconds = data?.details?.retry_after_seconds || 60;
        const retryTime = new Date(Date.now() + retryAfterSeconds * 1000);
        const timeString = retryTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        
        // Determine which limit was hit
        let limitMessage = 'Rate limit exceeded.';
        if (data?.details?.limits) {
          const limits = data.details.limits;
          const usage = data.details.current_usage || {};
          
          if (retryAfterSeconds <= 60) {
            limitMessage = `You've reached your per-minute limit (${usage.minute || 0}/${limits.tokens_per_minute} tokens).`;
          } else if (retryAfterSeconds <= 3600) {
            limitMessage = `You've reached your hourly limit (${usage.hour || 0}/${limits.tokens_per_hour} tokens).`;
          } else if (retryAfterSeconds <= 86400) {
            limitMessage = `You've reached your daily limit (${usage.day || 0}/${limits.tokens_per_day} tokens).`;
          } else {
            limitMessage = `You've reached your monthly limit.`;
          }
        }

        const errorMessage = `${limitMessage} Please try again after ${timeString}.`;
        
        if (currentStreamId) {
          updateMessage(currentStreamId, { content: errorMessage });
          completeStreamingMessage(currentStreamId);
          currentStreamId = null;
        } else {
          addMessage(createMessage(errorMessage, 'assistant'));
        }
      },
    });

    return () => {
      logger.info('🔌 Unsubscribing from session:', chatHash);
      setIsWebSocketReady(false);
      currentStreamId = null;
      unsubscribe();
    };
  }, [
    chatHash,
    sessionExists,
    subscribeToSession,
    addMessage,
    updateMessage,
    appendToStreamingMessage,
    completeStreamingMessage,
    startResponding,
    stopResponding,
    getToken,
    user?.email,
    setWaitingForResponse,
    persistQueuedFeedback,
  ]);

  // Send pending initial message when "ready"
  useEffect(() => {
    if (isWebSocketReady && pendingInitialMessage && !hasSentInitialMessageRef.current) {
      logger.info('🚀 WebSocket ready, sending pending initial message:', pendingInitialMessage);

      if (!hasDisplayedInitialUserMessageRef.current) {
        const userMessage = createMessage(pendingInitialMessage, 'user');
        addMessage(userMessage);
        hasDisplayedInitialUserMessageRef.current = true;
      }

      sendMessage(chatHash, pendingInitialMessage);

      setPendingInitialMessage(null);
      hasSentInitialMessageRef.current = true;
    }
  }, [isWebSocketReady, pendingInitialMessage, chatHash, sendMessage, addMessage]);

  const handleSendMessage = async (message: string) => {
    if (!chatHash || !isAuthenticated || sessionExists !== true) return;

    logger.info('📤 Sending message:', message);

    const userMessage = createMessage(message, 'user');
    addMessage(userMessage);
    setWaitingForResponse(true); // Show typing dots while waiting for AI

    // WebSocketContext now queues if not connected yet
    sendMessage(chatHash, message);

    // Check if title is still "New Chat" and generate from history if needed
    // This is the fallback for when NewChatPage couldn't generate a title (greeting detected)
    const currentTitle = currentSession?.title;
    if (
      currentTitle === 'New Chat' &&
      !isTitleGenerationInProgressRef.current
    ) {
      isTitleGenerationInProgressRef.current = true;
      logger.info('📝 [SessionChatPage] Title is still "New Chat", generating from history...');

      generateTitleFromHistory(chatHash, (streamingTitle: string) => {
        logger.info('📝 [SessionChatPage] Title streaming:', streamingTitle);
        updateSessionTitleLocally(chatHash, streamingTitle);
      }).then(({ title }) => {
        logger.info('✅ [SessionChatPage] Title generation from history complete:', title);
        updateSessionTitleLocally(chatHash, title);
        isTitleGenerationInProgressRef.current = false;
      }).catch((error: Error) => {
        logger.error('❌ [SessionChatPage] Failed to generate title from history:', error);
        isTitleGenerationInProgressRef.current = false;
      });
    }
  };

  const handleStopMessage = () => {
    logger.info('🛑 Stopping generation for session:', chatHash);
    stopGeneration(chatHash);
    stopResponding();
  };

  const handleRefreshSession = useCallback(async () => {
    logger.info('🔄 Refreshing session:', chatHash);
    setHasSessionUpdates(false);
    
    // Reload session messages from the server
    if (chatHash) {
      await loadSession(chatHash);
    }
  }, [chatHash, loadSession]);

  const handleLoadMoreMessages = useCallback(async () => {
    if (!chatHash) return;
    logger.info('📄 Loading more messages for session:', chatHash);
    await loadMoreMessages(chatHash);
  }, [chatHash, loadMoreMessages]);

  const handleDismissUpdate = useCallback(() => {
    logger.info('❌ Dismissing session update notification');
    setHasSessionUpdates(false);
  }, []);

  if (isLoadingMessages) {
    return (
      <div className={`${styles.chatContainer} ${sidebarCollapsed ? styles.sidebarCollapsed : styles.sidebarExpanded}`}>
        <div className={styles.chatContent}>
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}>
              <div className={styles.spinner}></div>
              <div className={styles.loadingText}>Loading session...</div>
            </div>
          </div>
        </div>
        <div className={styles.chatInputContainer}>
          <ChatInput
            onSend={() => {}}
            isBottom={true}
            isProcessing={true}
            isFirstMessage={false}
            isSessionBusy={false}
          />
        </div>
      </div>
    );
  }

  if (sessionExists === false) {
    return (
      <div className={`${styles.chatContainer} ${sidebarCollapsed ? styles.sidebarCollapsed : styles.sidebarExpanded}`}>
        <div className={styles.errorMessage}>
          <h3>Session not found</h3>
          <p>The chat session you're looking for doesn't exist or has been deleted.</p>
          <button onClick={() => navigate('/chat', { replace: true })}>Start New Chat</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.chatContainer} ${sidebarCollapsed ? styles.sidebarCollapsed : styles.sidebarExpanded}`}>
      <SessionUpdateBanner
        isVisible={hasSessionUpdates}
        onRefresh={handleRefreshSession}
        onDismiss={handleDismissUpdate}
      />
      <div className={styles.chatContent} ref={chatContentRef}>
        <ChatWindow
          messages={formattedMessages}
          isBottom={true}
          isWaitingForResponse={isWaitingForResponse}
          isStreaming={isResponding}
          onLoadMore={handleLoadMoreMessages}
          hasMore={hasMoreMessages}
          isLoadingMore={isLoadingMoreMessages}
          sessionId={chatHash}
          agentActivity={agentActivity}
          onMessageFeedbackChange={handleMessageFeedbackChange}
          onScrollButtonVisibilityChange={setShowScrollToBottom}
          onScrollToBottom={scrollToBottom}
        />
        <div ref={messagesEndRef} />
      </div>

      <div className={styles.chatInputContainer}>
        {showScrollToBottom && (
          <button
            className={styles.scrollToBottomButton}
            onClick={scrollToBottom}
            aria-label="Scroll to bottom"
          >
            <div className={styles.arrow}></div>
          </button>
        )}
        <ChatInput
          onSend={handleSendMessage}
          onStop={handleStopMessage}
          isBottom={true}
          isProcessing={isResponding || isWaitingForResponse}
          isFirstMessage={false}
          isSessionBusy={isSessionBusy}
        />
      </div>
    </div>
  );
};
